import { useState } from "react";
import { useForm } from "react-hook-form";
import { fetchJson } from "../../utils";

type FormData = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

export const AccountChangePassword = () => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();

    const onSubmit = async (data: FormData) => {
        if (data.newPassword !== data.confirmPassword) {
            setError("Passwords don't match");
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            
            const { json } = await fetchJson<{success: boolean; error: string}>('/api/account/change-password', {
                method: 'POST',
                body: JSON.stringify({
                    currentPassword: data.currentPassword,
                    newPassword: data.newPassword
                })
            });
            
            if (json.success) {
                setSuccess('Password changed successfully');
                reset();
            } else {
                setError(json.error);
            }
        } catch (err) {
            setError('Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <h1 className="section__title">Change Password</h1>
            <div className="persinfo">
                <div className="persinfo__form form">
                    {error && <p className="form__alert_global">{error}</p>}
                    {success && <p className="form__alert_success">{success}</p>}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={`form__row ${errors.currentPassword && 'is-error'}`}>
                            <label className="form__label">Current Password</label>
                            <div className="form__input-holder">
                                <input 
                                    type="password" 
                                    className="form__input"
                                    {...register('currentPassword', { required: 'Current password is required' })} 
                                />
                            </div>
                            {errors.currentPassword && <div className="form__alert">{errors.currentPassword.message}</div>}
                        </div>

                        <div className={`form__row ${errors.newPassword && 'is-error'}`}>
                            <label className="form__label">New Password</label>
                            <div className="form__input-holder">
                                <input 
                                    type="password" 
                                    className="form__input"
                                    {...register('newPassword', { 
                                        required: 'New password is required',
                                        minLength: {
                                            value: 8,
                                            message: 'Password must be at least 8 characters'
                                        }
                                    })} 
                                />
                            </div>
                            {errors.newPassword && <div className="form__alert">{errors.newPassword.message}</div>}
                        </div>

                        <div className={`form__row ${errors.confirmPassword && 'is-error'}`}>
                            <label className="form__label">Confirm New Password</label>
                            <div className="form__input-holder">
                                <input 
                                    type="password" 
                                    className="form__input"
                                    {...register('confirmPassword', { required: 'Please confirm your password' })} 
                                />
                            </div>
                            {errors.confirmPassword && <div className="form__alert">{errors.confirmPassword.message}</div>}
                        </div>

                        <div className="form__row form__row_smallgap form__btns">
                            <button 
                                className="btn btn_cta btn_lg btn_extrapad" 
                                type="submit"
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Change Password'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AccountChangePassword;